import React, { useEffect, useState } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import { VideoHighlight, videoHighlightsService } from '../../../shared/services/videoHighlightsService';
import { useAuth } from '../../../shared/contexts/AuthContext';

interface EditHighlightModalProps {
  isOpen: boolean;
  highlight: VideoHighlight | null;
  highlightId?: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const EditHighlightModal: React.FC<EditHighlightModalProps> = ({
  isOpen,
  highlight,
  highlightId,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentHighlight, setCurrentHighlight] = useState<VideoHighlight | null>(highlight);
  const [isLoading, setIsLoading] = useState(false);

  // Load highlight if highlightId is provided but highlight is not
  useEffect(() => {
    if (isOpen && highlightId && !highlight) {
      setIsLoading(true);
      videoHighlightsService.getHighlightById(highlightId)
        .then((result) => {
          if (result.success && result.data) {
            setCurrentHighlight(result.data);
          }
        })
        .catch((err) => {
          console.error('Error loading highlight:', err);
          setError('Failed to load highlight data');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else if (highlight) {
      setCurrentHighlight(highlight);
    }
  }, [isOpen, highlightId, highlight]);

  useEffect(() => {
    if (currentHighlight) {
      setName(currentHighlight.name);
      setIsActive(currentHighlight.is_active);
      setError(null);
    } else {
      setName('');
      setIsActive(true);
    }
  }, [currentHighlight]);

  // Check if highlight has any storage files
  const hasStorageFiles = Boolean(
    currentHighlight?.output_storage_path ||
    currentHighlight?.enhanced_storage_path ||
    currentHighlight?.output_url ||
    currentHighlight?.enhanced_url
  );

  // Disable is_active toggle if files exist
  const canDeactivate = !hasStorageFiles;

  const handleSubmit = async () => {
    if (!currentHighlight || !user?.id) return;
    if (!name.trim()) {
      setError('Highlight name is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Update highlight name and is_active
      const updatePayload: any = {
        name: name.trim(),
        updatedBy: user.id,
      };

      // Update name via updateHighlight
      const result = await videoHighlightsService.updateHighlight(currentHighlight.id, updatePayload);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to update highlight');
      }

      // Update is_active separately if it changed (updateHighlight doesn't support it directly)
      if (currentHighlight.is_active !== isActive) {
        const activeResult = await videoHighlightsService.updateHighlightIsActive(currentHighlight.id, isActive, user.id);
        if (!activeResult.success) {
          throw new Error(activeResult.error || 'Failed to update highlight status');
        }
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update highlight');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Highlight" className="max-w-lg">
      <div className="p-6 space-y-4">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-2 text-sm text-gray-500">Loading highlight data...</span>
          </div>
        )}
        {!isLoading && !currentHighlight && (
          <div className="text-center py-8">
            <p className="text-sm text-red-600">Failed to load highlight data</p>
          </div>
        )}
        {!isLoading && currentHighlight && (
          <>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Highlight Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter highlight name"
            maxLength={80}
          />
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <p className="text-xs text-gray-500">
              {canDeactivate
                ? 'Inactive highlights won\'t appear in the list'
                : 'Cannot deactivate highlight with existing files. Delete files first.'}
            </p>
          </div>
          <label className={`flex items-center ${canDeactivate ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => {
                if (canDeactivate) {
                  setIsActive(e.target.checked);
                }
              }}
              disabled={!canDeactivate}
              className="sr-only"
            />
            <div
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isActive ? 'bg-blue-600' : 'bg-gray-300'
              } ${canDeactivate ? '' : 'opacity-50'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </div>
            <span className="ml-3 text-sm font-medium text-gray-700">
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </label>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving || !name.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default EditHighlightModal;

